import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWeb3 } from '../context/Web3Context';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PageHero } from '../components/PageHero';
import { CheckCircle2, Circle, Trophy, Flame, Sparkles, Gift, Crown, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
    'Débutant': '#9CA3AF',
    'Régulier': '#10B981',
    'Sérieux': '#3B82F6',
    'Diamant': '#8B5CF6',
    'Légende': '#FBBF24',
};

export default function QuestsPage() {
    const { t } = useTranslation();
    const { address } = useWeb3();
    const [data, setData] = useState(null);
    const [stats, setStats] = useState(null);
    const [claiming, setClaiming] = useState(false);

    const load = async () => {
        if (!address) return;
        try {
            const [t, s] = await Promise.all([
                fetch(`${API}/api/quests/${address}/today`).then(r => r.json()),
                fetch(`${API}/api/quests/stats`).then(r => r.json()),
            ]);
            setData(t);
            setStats(s);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { load(); const i = setInterval(load, 20000); return () => clearInterval(i); }, [address]);

    const claim = async () => {
        if (!address) return;
        setClaiming(true);
        try {
            const res = await fetch(`${API}/api/quests/${address}/claim`, { method: 'POST' });
            const d = await res.json();
            if (d.success && d.total_reward_b4p > 0) {
                const usdPart = d.total_reward_usd ? ` (≈ $${d.total_reward_usd})` : '';
                toast.success(`+${d.total_reward_b4p} B4P réclamés${usdPart} (×${d.streak_multiplier} streak)`);
            } else if (d.total_reward_b4p === 0) {
                toast.info('Aucune récompense à réclamer (complétez plus de quêtes !)');
            } else {
                toast.error(d.error || 'Erreur');
            }
            load();
        } catch (e) { toast.error('Erreur'); }
        setClaiming(false);
    };

    const completed = data?.quests?.filter(q => q.completed).length || 0;
    const total = data?.quests?.length || 5;
    const streak = data?.streak || {};
    const tierColor = TIER_COLORS[streak.label] || '#9CA3AF';

    if (!address) {
        return (
            <div className="min-h-screen" data-testid="quests-page-no-wallet">
                <PageHero
                icon={Gift}
                title={t('quests.title')}
                highlight={t('quests.highlight')}
                subtitle={t('quests.subtitle')}
            />
                <div className="flex items-center justify-center px-4 py-12">
                    <Card className="bg-white/5 border-white/10 max-w-md">
                        <CardContent className="p-8 text-center">
                            <Lock className="w-12 h-12 text-white/40 mx-auto mb-4" />
                            <h2 className="text-2xl font-bold text-white mb-2">{t('quests.noWalletTitle')}</h2>
                            <p className="text-white/60">{t('quests.noWalletDesc')}</p>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" data-testid="quests-page">
            <PageHero
                icon={Gift}
                title={t('quests.title')}
                highlight={t('quests.highlight')}
                subtitle={t('quests.subtitle')}
            />
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">

                {/* Streak hero */}
                <Card className="bg-gradient-to-br from-black via-black to-[#FF6B00]/15 border-[#FF6B00]/30 mb-8 overflow-hidden relative" data-testid="streak-hero">
                    <CardContent className="p-6 flex flex-wrap items-center justify-between gap-6">
                        <div className="flex items-center gap-5">
                            <div className="relative">
                                <Flame className="w-20 h-20" style={{ color: tierColor }} />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-4xl font-black text-white" data-testid="streak-days">{streak.current_streak || 0}</span>
                                </div>
                            </div>
                            <div>
                                <Badge style={{ backgroundColor: `${tierColor}30`, color: tierColor, borderColor: tierColor }} className="mb-2">{streak.label || 'Débutant'}</Badge>
                                <div className="text-3xl font-bold text-white">×{streak.multiplier || 1}<span className="text-lg text-white/50 ml-2">multiplicateur</span></div>
                                <div className="text-sm text-white/50 mt-1">Meilleur streak : {streak.best_streak || 0} jours</div>
                            </div>
                        </div>
                        {streak.next_tier && (
                            <div className="text-right">
                                <div className="text-xs text-white/50 uppercase mb-1">Prochain palier</div>
                                <div className="text-xl font-bold" style={{ color: TIER_COLORS[streak.next_tier.label] }}>{streak.next_tier.label} ×{streak.next_tier.multiplier}</div>
                                <div className="text-sm text-white/60">dans {streak.days_to_next_tier} jours</div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Today's progress */}
                <Card className="bg-white/5 border-white/10 mb-6">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center justify-between">
                            <span className="flex items-center gap-2"><Gift className="w-5 h-5" />Progression du jour</span>
                            <Badge className="bg-[#00FF66]/15 text-[#00FF66] border-[#00FF66]/30" data-testid="completion-badge">{completed} / {total}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-3">
                            <div className="h-full bg-gradient-to-r from-[#00FF66] to-[#FFD700] transition-all" style={{ width: `${(completed / total) * 100}%` }} />
                        </div>
                        <div className="text-xs text-white/60">
                            {completed >= 3 ? 'Streak du jour validé — votre série continue.' : `Encore ${3 - completed} quêtes pour valider la série du jour.`}
                        </div>
                    </CardContent>
                </Card>

                {/* Quests list */}
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                    {(data?.quests || []).map((q, i) => (
                        <Card key={q.quest_id + i} className={`border ${q.completed ? 'border-[#00FF66]/40 bg-[#00FF66]/5' : 'border-white/10 bg-white/5'} transition-all`} data-testid={`quest-${q.quest_id}`}>
                            <CardContent className="p-4 flex items-center gap-3">
                                {q.completed ? (
                                    <CheckCircle2 className="w-7 h-7 text-[#00FF66] flex-shrink-0" />
                                ) : (
                                    <Circle className="w-7 h-7 text-white/30 flex-shrink-0" />
                                )}
                                <div className="flex-1">
                                    <div className="text-white font-medium text-sm">{q.label}</div>
                                    <div className="text-xs text-white/50 mt-0.5">Progression {q.progress}/{q.target}</div>
                                </div>
                                <div className="text-right" data-testid={`quest-reward-${q.quest_id}`}>
                                    <Badge className={q.claimed ? 'bg-white/10 text-white/40' : 'bg-[#FFD700]/15 text-[#FFD700] border-[#FFD700]/30'}>
                                        {q.claimed ? '✓' : `+${q.reward_b4p_preview ?? q.reward_b4p ?? 0}`} B4P
                                    </Badge>
                                    {!q.claimed && q.reward_usd != null && (
                                        <div className="text-[10px] text-white/40 mt-0.5">≈ ${q.reward_usd}</div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                <Button onClick={claim} disabled={claiming || completed === 0} className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold h-12 mb-8" data-testid="claim-quests-btn">
                    {claiming ? '...' : `Réclamer ${completed} récompense(s) (×${streak.multiplier || 1})`}
                </Button>

                {/* Tiers + Leaderboard */}
                <div className="grid md:grid-cols-2 gap-6">
                    <Card className="bg-white/5 border-white/10">
                        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Crown className="w-5 h-5 text-[#FFD700]" />Paliers de streak</CardTitle></CardHeader>
                        <CardContent className="space-y-2">
                            {(stats?.tiers || []).map(t => (
                                <div key={t.label} className="flex items-center justify-between p-2 rounded-lg bg-black/20">
                                    <div className="flex items-center gap-2">
                                        <Flame className="w-4 h-4" style={{ color: TIER_COLORS[t.label] }} />
                                        <span className="text-white">{t.label}</span>
                                        <span className="text-xs text-white/40">≥{t.min_days}j</span>
                                    </div>
                                    <Badge style={{ backgroundColor: `${TIER_COLORS[t.label]}20`, color: TIER_COLORS[t.label] }}>×{t.multiplier}</Badge>
                                </div>
                            ))}
                        </CardContent>
                    </Card>

                    <Card className="bg-white/5 border-white/10">
                        <CardHeader><CardTitle className="text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FFD700]" />Top streakers</CardTitle></CardHeader>
                        <CardContent>
                            {(stats?.leaderboard || []).length === 0 ? (
                                <div className="text-center text-white/40 py-4 text-sm">Soyez le premier à débuter une série !</div>
                            ) : (
                                <div className="space-y-2">
                                    {stats.leaderboard.map((u, idx) => (
                                        <div key={u.address} className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                                            <div className="flex items-center gap-2">
                                                <span className="text-white/40 text-sm w-5">#{idx + 1}</span>
                                                <span className="text-white font-mono text-sm">{u.address.slice(0, 8)}...{u.address.slice(-4)}</span>
                                            </div>
                                            <div className="flex items-center gap-1 text-[#FF6B00] font-bold"><Flame className="w-3 h-3" />{u.current_streak}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}
